import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, spring, interpolate } from 'remotion';

interface RecognitionStep {
  text: string;
  confidence: number;
  time: number;
}

interface StatusInfo {
  label: string;
  statusText: string;
  language: string;
  model: string;
}

export interface AIVoiceRecognitionProps {
  title?: string;
  subtitle?: string;
  background?: string;
  accentColor?: string;
  frequencyBands?: number;
  spectrumRadius?: number;
  recognitionSteps?: RecognitionStep[];
  stepInterval?: number;
  statusInfo?: StatusInfo;
  volumeLabel?: string;
  resultLabel?: string;
}

const defaultRecognitionSteps: RecognitionStep[] = [
  { text: '你', confidence: 0.95, time: 0 },
  { text: '好', confidence: 0.92, time: 0.5 },
  { text: '世', confidence: 0.88, time: 1.0 },
  { text: '界', confidence: 0.96, time: 1.5 },
];

const defaultStatusInfo: StatusInfo = {
  label: '状态',
  statusText: '正在识别...',
  language: '中文',
  model: 'Whisper Large v3',
};

export const AIVoiceRecognition: React.FC<AIVoiceRecognitionProps> = ({
  title = 'AI Voice Recognition',
  subtitle = '实时语音识别可视化',
  background = 'linear-gradient(135deg, #0a0a0a 0%, #0a1a2e 100%)',
  accentColor = '#4fc3f7',
  frequencyBands = 32,
  spectrumRadius: spectrumRadiusProp = 150,
  recognitionSteps = defaultRecognitionSteps,
  stepInterval = 0.5,
  statusInfo = defaultStatusInfo,
  volumeLabel = '音量',
  resultLabel = '识别结果',
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  const titleProgress = spring({ frame, fps, config: { damping: 200 } });
  const waveformProgress = spring({ frame: frame - 10, fps, config: { damping: 200 } });

  const waveform = Array.from({ length: frequencyBands }, (_, i) => {
    const phase = frame / fps * 3 + i * 0.2;
    const baseHeight = Math.sin(phase) * 50 + Math.sin(phase * 2.5) * 30 + Math.sin(phase * 0.5) * 40;
    return Math.abs(baseHeight) * waveformProgress;
  });

  const currentTime = frame / fps;
  const currentStep = Math.floor(currentTime / stepInterval);

  const waveformWidth = width * 0.8;
  const bandWidth = waveformWidth / frequencyBands;
  const waveformStartX = (width - waveformWidth) / 2;
  const waveformStartY = height * 0.3;

  const spectrumCenterX = width / 2;
  const spectrumCenterY = height * 0.6;
  const spectrumRadius = spectrumRadiusProp;

  return (
    <AbsoluteFill style={{ background }}>
      <div
        style={{
          position: 'absolute',
          top: 60,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: titleProgress,
          transform: `translateY(${interpolate(titleProgress, [0, 1], [-30, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 80,
            fontWeight: 'bold',
            color: accentColor,
            textShadow: `0 0 20px ${accentColor}80`,
            margin: 0,
          }}
        >
          {title}
        </h1>
        <p
          style={{
            fontSize: 32,
            color: '#aaa',
            marginTop: 10,
          }}
        >
          {subtitle}
        </p>
      </div>

      {/* 音频波形 */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        <defs>
          <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={accentColor} />
            <stop offset="50%" stopColor={accentColor} stopOpacity={0.8} />
            <stop offset="100%" stopColor={accentColor} stopOpacity={0.6} />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* 波形柱 */}
        {waveform.map((h, i) => {
          const x = waveformStartX + i * bandWidth;
          const y = waveformStartY - h / 2;
          const opacity = spring({ frame: frame - 10 - i * 0.5, fps, config: { damping: 200 } });

          return (
            <rect
              key={i}
              x={x}
              y={y}
              width={bandWidth - 2}
              height={h}
              fill="url(#waveGradient)"
              opacity={opacity}
              filter="url(#glow)"
              rx={2}
            />
          );
        })}

        {/* 频谱圆形 */}
        {waveform.map((h, i) => {
          const angle = (i / frequencyBands) * Math.PI * 2 - Math.PI / 2;
          const x1 = spectrumCenterX + Math.cos(angle) * spectrumRadius;
          const y1 = spectrumCenterY + Math.sin(angle) * spectrumRadius;
          const x2 = spectrumCenterX + Math.cos(angle) * (spectrumRadius + h * 0.5);
          const y2 = spectrumCenterY + Math.sin(angle) * (spectrumRadius + h * 0.5);

          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={accentColor}
              strokeWidth={3}
              opacity={waveformProgress}
              filter="url(#glow)"
            />
          );
        })}

        {/* 中心圆 */}
        <circle
          cx={spectrumCenterX}
          cy={spectrumCenterY}
          r={spectrumRadius - 10}
          fill="none"
          stroke={accentColor}
          strokeWidth={2}
          opacity={0.5}
        />
      </svg>

      {/* 识别结果 */}
      <div
        style={{
          position: 'absolute',
          bottom: 320,
          left: 0,
          right: 0,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 28, color: '#aaa', marginBottom: 22 }}>{resultLabel}</div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 20 }}>
          {recognitionSteps.map((step, i) => {
            const showStep = i <= currentStep;
            const progress = showStep ? spring({ frame: frame - 15 - i * 15, fps, config: { damping: 15, stiffness: 80 } }) : 0;
            const confidenceColor = step.confidence > 0.9 ? '#4caf50' : step.confidence > 0.8 ? '#ff9800' : '#f44336';

            return (
              <div
                key={i}
                style={{
                  opacity: progress,
                  transform: `scale(${progress}) translateY(${interpolate(progress, [0, 1], [20, 0])}px)`,
                }}
              >
                <div
                  style={{
                    fontSize: 80,
                    fontWeight: 'bold',
                    color: '#fff',
                    textShadow: '0 0 20px rgba(255, 255, 255, 0.3)',
                  }}
                >
                  {step.text}
                </div>
                <div
                  style={{
                    fontSize: 24,
                    color: confidenceColor,
                    marginTop: 5,
                  }}
                >
                  {(step.confidence * 100).toFixed(0)}%
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 音量指示器 */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: 60,
          right: 60,
          opacity: spring({ frame: frame - 20, fps, config: { damping: 200 } }),
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
          <div style={{ fontSize: 24, color: '#aaa' }}>{volumeLabel}</div>
          <div style={{ flex: 1, height: 20, background: 'rgba(255, 255, 255, 0.1)', borderRadius: 10, overflow: 'hidden' }}>
            <div
              style={{
                width: `${50 + Math.sin(frame / fps * 5) * 30}%`,
                height: '100%',
                background: `linear-gradient(90deg, ${accentColor} 0%, ${accentColor}cc 100%)`,
                borderRadius: 10,
                transition: 'width 0.1s',
              }}
            />
          </div>
          <div style={{ fontSize: 24, color: accentColor, fontWeight: 'bold' }}>
            {Math.round(50 + Math.sin(frame / fps * 5) * 30)}%
          </div>
        </div>
      </div>

      {/* 状态信息 */}
      <div
        style={{
          position: 'absolute',
          top: 70,
          right: 60,
          opacity: spring({ frame: frame - 25, fps, config: { damping: 200 } }),
        }}
      >
        <div
          style={{
            background: `${accentColor}1a`,
            border: `2px solid ${accentColor}`,
            borderRadius: 8,
            padding: '15px 25px',
          }}
        >
          <div style={{ fontSize: 24, color: '#aaa' }}>{statusInfo.label}</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#4caf50', marginTop: 5 }}>
            {statusInfo.statusText}
          </div>
          <div style={{ fontSize: 20, color: '#aaa', marginTop: 10 }}>
            语言：{statusInfo.language}
          </div>
          <div style={{ fontSize: 20, color: '#aaa', marginTop: 5 }}>
            模型：{statusInfo.model}
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};
